import * as React from 'react';
import { Column, Columns, div, FormSelect, Input, Option, CompactCheckbox, asCurrency } from 'lqx-react';
import { ErrorHighligter, InputWithMask, YesNoSelect } from '../../shared';
import { Policy } from '../../api/policy';
import { PolicyLabel } from './shared';
import { makeStyles } from '@material-ui/core/styles';
import { useLinked } from '@type-r/react';
import { PolicyAttachmentTypes, PolicyDeductibleTypes, PremiumBasisTypes, ScopeOfCoverageTypes, YesNoTypes } from '../../api/reference-data';
import { DigiSelect } from '../../components/select';

export const PolicyParametersForm = ({ policy, validate }: { policy: Policy, validate }) => {
    const classes = useStyles();
    const $checkedInsolvency = useLinked(policy.perilCoverageWaitingPeriods.filter(p => p.perilCoverageType.name === "Insolvency"));
    const $checkedPoliticalRisks = useLinked(policy.perilCoverageWaitingPeriods.filter(p => p.perilCoverageType.name === "Political Risks"));
    const $checkedProtractedDefault = useLinked(policy.perilCoverageWaitingPeriods.filter(p => p.perilCoverageType.name === "Protracted Default"));

    //TODO: not on the model
    const $coverageType = useLinked(null);
    const $invoiceTermsType = useLinked(null);
    const $nonQualifyingLossType = useLinked(null);

    return (<>
        <Columns>
            <Column><PolicyLabel>Auto Extension *</PolicyLabel></Column>
            <Column><PolicyLabel>Value (months)</PolicyLabel></Column>
            <Column><PolicyLabel></PolicyLabel></Column>
            <Column><PolicyLabel></PolicyLabel></Column>
        </Columns>
        <Columns>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'autoExtension' ] }>
                    <FormSelect  data-qa-id="auto extension" { ...{ onBlur: validate } } $value={ policy.$.autoExtension } >
                        { YesNoTypes.map(option => <Option key={ option.id } value={ option.id }>{ option.name }</Option>) }
                    </FormSelect>
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'maxExtensionPeriod' ] }>
                    <Input onBlur={ validate } number $value={ policy.$.maxExtensionPeriod } />
                </ErrorHighligter>
            </Column>
            <Column>
            </Column>
            <Column>
            </Column>
        </Columns>
        <Columns>
            <Column><PolicyLabel>Policy Limit of Liability *</PolicyLabel></Column>
            <Column><PolicyLabel>Indemnity (%) *</PolicyLabel></Column>
            <Column><PolicyLabel>Premium Convention (%) *</PolicyLabel></Column>
            <Column><PolicyLabel>Premium Basis *</PolicyLabel></Column>
        </Columns>
        <Columns>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'liabilityLimit' ] }>
                    <InputWithMask data-qa-id="policy limit of liability" onBlur={ validate } number $value={ policy.$.liabilityLimit } mask={ asCurrency(policy.liabilityLimit) } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'indemnity' ] }>
                    <InputWithMask data-qa-id="Indemnity" onBlur={ validate } number $value={ policy.$.indemnity } mask={ policy.indemnity.toFixed(2) + '%' } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'premiumRate' ] }>
                    <InputWithMask data-qa-id="premium convention (%)" onBlur={ validate } number $value={ policy.$.premiumRate } mask={ policy.premiumRate.toFixed(4) + '%' } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'premiumBasisType' ] }>
                    <DigiSelect { ...{ onBlur: validate } } data-qa-id="premium basis" id="id" label="name" $value={ policy.$.premiumBasisType } options={ PremiumBasisTypes } />
                </ErrorHighligter>
            </Column>
        </Columns>
        <Columns>
            <Column><PolicyLabel>Policy Currency *</PolicyLabel></Column>
            <Column><PolicyLabel>Estimated Longest Payment Terms *</PolicyLabel></Column>
            <Column><PolicyLabel>Scope of Coverage *</PolicyLabel></Column>
            <Column><PolicyLabel>Attachment Type *</PolicyLabel></Column>
        </Columns>
        <Columns>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'currencyCode' ] }>
                    <FormSelect data-qa-id="policy currency" { ...{ onBlur: validate } } $value={ policy.$.currencyCode } >
                        <Option value="AUD">AUD</Option>
                        <Option value="CAD">CAD</Option>
                        <Option value="CHF">CHF</Option>
                        <Option value="EUR">EUR</Option>
                        <Option value="GBP">GBP</Option>
                        <Option value="SGD">SGD</Option>
                        <Option value="USD">USD</Option>
                    </FormSelect>
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'maxPaymentTerms' ] }>
                    <Input data-qa-id="estimated longest payment terms" onBlur={ validate } number $value={ policy.$.maxPaymentTerms } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'scopeOfCoverageType' ] }>
                    <DigiSelect { ...{ onBlur: validate } } data-qa-id="scope of coverage" id="id" label="name" $value={ policy.$.scopeOfCoverageType } options={ ScopeOfCoverageTypes } />
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'policyAttachmentType' ] }>
                    <DigiSelect { ...{ onBlur: validate } } data-qa-id="attachment type" id="id" label="name" $value={ policy.$.policyAttachmentType } options={ PolicyAttachmentTypes } />
                </ErrorHighligter>
            </Column>
        </Columns>
        <Columns>
            <Column><PolicyLabel>Coverage Type *</PolicyLabel></Column>
            <Column><PolicyLabel>Claim Filing Period *</PolicyLabel></Column>
            <Column>
                <Columns>
                    <Column><PolicyLabel>All Risks</PolicyLabel></Column>
                    <Column><PolicyLabel>Insolvency</PolicyLabel></Column>
                </Columns>
            </Column>
            <Column>
                <Columns>
                    <Column><PolicyLabel>Protracted Default</PolicyLabel></Column>
                    <Column><PolicyLabel>Political Risks</PolicyLabel></Column>
                </Columns>
            </Column>
        </Columns>
        <Columns>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'coverageType' ] }>
                    <FormSelect  data-qa-id="coverage type" { ...{ onBlur: validate } } $value={ $coverageType } >
                        <Option value="All Perils">All Perils</Option>
                        <Option value="Named Perils">Named Perils</Option>
                    </FormSelect>
                </ErrorHighligter>
            </Column>
            <Column>
                <ErrorHighligter error={ policy.errors[ 'claimFilingPeriodPerilCoverPeriodType' ] }>
                    <FormSelect data-qa-id="claim filing period" { ...{ onBlur: validate } } $value={ policy.claimFilingPeriodPerilCoverPeriodType.$.name } >
                        <Option value="Not Applicable">Not Applicable</Option>
                        <Option value="Enter Value">Enter Value</Option>
                    </FormSelect>
                </ErrorHighligter>
            </Column>
            <Column>
                <Columns>
                    <Column>
                        <ErrorHighligter error={ policy.errors[ 'claimFilingPeriodAllRisk' ] }>
                            <Input onBlur={ validate } $value={ policy.policyCountrySchedules.models[ 0 ].$.claimFilingPeriodAllRisk } />
                        </ErrorHighligter>
                    </Column>
                    <Column>
                        <ErrorHighligter error={ policy.errors[ 'claimFilingPeriodInsolvency' ] }>
                            <Input onBlur={ validate } $value={ policy.policyCountrySchedules.models[ 0 ].$.claimFilingPeriodInsolvency } />
                        </ErrorHighligter>
                    </Column>
                </Columns>
            </Column>
            <Column>
                <Columns>
                    <Column>
                        <ErrorHighligter error={ policy.errors[ 'claimFilingPeriodProtractedDefault' ] }>
                            <Input onBlur={ validate } $value={ policy.policyCountrySchedules.models[ 0 ].$.claimFilingPeriodProtractedDefault } />
                        </ErrorHighligter>
                    </Column>
                    <Column>
                        <ErrorHighligter error={ policy.errors[ 'claimFilingPoliticalRisk' ] }>
                            <Input onBlur={ validate } $value={ policy.policyCountrySchedules.models[ 0 ].$.claimFilingPoliticalRisk } />
                        </ErrorHighligter>
                    </Column>
                </Columns>
            </Column>
        </Columns>
        <Columns>
            <Column><PolicyLabel>Names Perils</PolicyLabel></Column>
            <Column><PolicyLabel>Waiting Period *</PolicyLabel></Column>
            <Column>
                <Columns>
                    <Column><PolicyLabel>All Risks</PolicyLabel></Column>
                    <Column><PolicyLabel>Insolvency</PolicyLabel></Column>
                </Columns>
            </Column>
            <Column>
                <Columns>
                    <Column><PolicyLabel>Protracted Default</PolicyLabel></Column>
                    <Column><PolicyLabel>Political Risks</PolicyLabel></Column>
                </Columns>
            </Column>
        </Columns>
        <Columns>
            <Column>
                <div style={ { backgroundColor: '#F8F8F8' } }>
                    <CompactCheckbox readOnly={ false } classes={ classes } $checked={ $checkedInsolvency } label="Insolvency" />
                    <CompactCheckbox readOnly={ false } classes={ classes } $checked={ $checkedPoliticalRisks } label="Political Risks" />
                    <CompactCheckbox readOnly={ false } classes={ classes } $checked={ $checkedProtractedDefault } label="Protracted Default" />
                </div>
                <div></div>
            </Column>
            <Column>
                <div>
                    <ErrorHighligter error={ policy.errors[ 'perilCoverageType' ] }>
                        <FormSelect data-qa-id="waiting period" { ...{ onBlur: validate } } $value={ policy.perilCoverageWaitingPeriods.models[ 0 ].perilCoverageType.$.name } >
                            <Option value="All Perils">All Perils</Option>
                            <Option value="Named Perils">Named Perils</Option>
                            <Option value="Enter Value">Enter Value</Option>
                        </FormSelect>
                    </ErrorHighligter>
                </div>
            </Column>
            <Column>
                <Columns>
                    <Column>
                        <ErrorHighligter error={ policy.errors[ 'waitingPeriodAllRisk' ] }>
                            <Input onBlur={ validate } $value={ policy.policyCountrySchedules.models[ 0 ].$.waitingPeriodAllRisk } />
                        </ErrorHighligter>
                    </Column>
                    <Column>
                        <ErrorHighligter error={ policy.errors[ 'waitingPeriodInsolvency' ] }>
                            <Input onBlur={ validate } $value={ policy.policyCountrySchedules.models[ 0 ].$.waitingPeriodInsolvency } />
                        </ErrorHighligter>
                    </Column>
                </Columns>
            </Column>
            <Column>
                <Columns>
                    <Column>
                        <ErrorHighligter error={ policy.errors[ 'waitingPeriodProtractedDefault' ] }>
                            <Input onBlur={ validate } $value={ policy.policyCountrySchedules.models[ 0 ].$.waitingPeriodProtractedDefault } />
                        </ErrorHighligter>
                    </Column>
                    <Column>
                        <ErrorHighligter error={ policy.errors[ 'waitingPeriodPoliticalRisk' ] }>
                            <Input onBlur={ validate } $value={ policy.policyCountrySchedules.models[ 0 ].$.waitingPeriodPoliticalRisk } />
                        </ErrorHighligter>
                    </Column>
                </Columns>
            </Column>
        </Columns>

        <PolicyParametersBottomSection>
            <Columns>
                <Column><PolicyLabel>Invoicing Period</PolicyLabel></Column>
                <Column><PolicyLabel>Value (days)</PolicyLabel></Column>
                <Column><PolicyLabel>Non Qualifying Loss *</PolicyLabel></Column>
                <Column><PolicyLabel>Value ($)</PolicyLabel></Column>
            </Columns>
            <Columns>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'invoiceTermsType' ] }>
                        <YesNoSelect  { ...{ onBlur: validate } } $value={ $invoiceTermsType } />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'invoiceTerms' ] }>
                        <Input onBlur={ validate } $value={ policy.$.invoiceTerms } />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'nonQualifyingLossType' ] }>
                        <YesNoSelect  { ...{ onBlur: validate } } $value={ $nonQualifyingLossType } />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'nonQualifyingLoss' ] }>
                        <InputWithMask onBlur={ validate } number $value={ policy.policyCountrySchedules.models[ 0 ].$.nonQualifyingLoss } mask={ asCurrency(policy.policyCountrySchedules.models[ 0 ].$.nonQualifyingLoss) } />
                    </ErrorHighligter>
                </Column>
            </Columns>
            <Columns>
                <Column><PolicyLabel>Deductible? *</PolicyLabel></Column>
                <Column><PolicyLabel>Value ($)</PolicyLabel></Column>
                <Column><PolicyLabel>DCL</PolicyLabel></Column>
                <Column><PolicyLabel>Value ($)</PolicyLabel></Column>
            </Columns>
            <Columns>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'policyDeductibleType' ] }>
                        <DigiSelect { ...{ onBlur: validate } } id="id" label="name" $value={ policy.$.policyDeductibleType } options={ PolicyDeductibleTypes } />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'deductibleAmount' ] }>
                        <Input onBlur={ validate } number $value={ policy.$.deductibleAmount } />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'havingDiscretionCreditLimit' ] }>
                        <YesNoSelect  { ...{ onBlur: validate } } $value={ policy.$.havingDiscretionCreditLimit } />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'discretionaryCreditLimit' ] }>
                        <Input onBlur={ validate } number $value={ policy.$.discretionaryCreditLimit } />
                    </ErrorHighligter>
                </Column>
            </Columns>
            <Columns>
                <Column><PolicyLabel>Amount Past Due Reporting Threshold</PolicyLabel></Column>
                <Column><PolicyLabel>Days Past Due Reporting Threshold</PolicyLabel></Column>
                <Column><PolicyLabel>Cancellable? *</PolicyLabel></Column>
                <Column></Column>
            </Columns>
            <Columns>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'amountPastDueRptThreshold' ] }>
                        <InputWithMask onBlur={ validate } number $value={ policy.policyPremiumSchedule.$.amountPastDueRptThreshold } mask={ asCurrency(policy.policyPremiumSchedule.amountPastDueRptThreshold) } />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'daysPastDueRptThreshold' ] }>
                        <Input onBlur={ validate } number $value={ policy.policyPremiumSchedule.$.daysPastDueRptThreshold } />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'cancellableLimitType' ] }>
                        <DigiSelect { ...{ onBlur: validate } } id="id" label="name" $value={ policy.$.cancellableLimitType } options={ YesNoTypes } />
                    </ErrorHighligter>
                </Column>
                <Column>
                </Column>
            </Columns>
            <Columns>
                <Column><PolicyLabel>Max Extension Period</PolicyLabel></Column>
                <Column><PolicyLabel>Value (days)</PolicyLabel></Column>
                <Column><PolicyLabel>Eligible Governing Laws</PolicyLabel></Column>
                <Column><PolicyLabel>Eligible Transaction Currencies</PolicyLabel></Column>
            </Columns>
            <Columns>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'maxExtensionPeriodType' ] }>
                        <DigiSelect { ...{ onBlur: validate } } id="id" label="name" $value={ policy.$.maxExtensionPeriodType } options={ YesNoTypes } />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'maxExtensionPeriod' ] }>
                        <Input onBlur={ validate } number $value={ policy.$.maxExtensionPeriod } />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'policyGoverningLaws' ] }>
                        <Input onBlur={ validate } $value={ policy.policyGoverningLaws.models[ 0 ].$.name } />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'policyTransactionCurrencyCodes' ] }>
                        <FormSelect  { ...{ onBlur: validate } } $value={ policy.policyTransactionCurrencyCodes.models[ 0 ].$.code } >
                            <Option value="AUD">AUD</Option>
                            <Option value="CAD">CAD</Option>
                            <Option value="CHF">CHF</Option>
                            <Option value="EUR">EUR</Option>
                            <Option value="GBP">GBP</Option>
                            <Option value="SGD">SGD</Option>
                            <Option value="USD">USD</Option>
                            <Option value="ANY">ANY</Option>
                        </FormSelect>
                    </ErrorHighligter>
                </Column>
            </Columns>
            <Columns>
                <Column><PolicyLabel>Deal Team</PolicyLabel></Column>
                <Column><PolicyLabel>Collateral Agent</PolicyLabel></Column>
                <Column></Column>
                <Column></Column>
            </Columns>
            <Columns>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'dealTeam' ] }>
                        <Input onBlur={ validate } $value={ policy.dealTeam.models[ 0 ].$.userName } />
                    </ErrorHighligter>
                </Column>
                <Column>
                    <ErrorHighligter error={ policy.errors[ 'collateralAgent' ] }>
                        <Input onBlur={ validate } $value={ policy.collateralAgent.$.firstName } />
                    </ErrorHighligter>
                </Column>
                <Column>
                </Column>
                <Column>
                </Column>
            </Columns>
        </PolicyParametersBottomSection>
    </>
    )
};

const PolicyParametersBottomSection = div({
    marginTop: '90px'
});


const useStyles = makeStyles({
    label: {
        textTransform: "capitalize",
        fontSize: "11px",
        color: "black",
        fontWeight: 500,
        paddingLeft: '0px'
    }
})

